import React, { useEffect, useState } from 'react';
import { X, Clock } from 'lucide-react';
import ChessRook from './ChessRook';
import thumb from '../utils/thumb';

// ──────────────────────────────────────────────────────────────────────
// Card picker for the PR Manager sheet.
// Shows the player's currently-exhibiting paintings that are not yet
// managed by Molly. Tap a card → resolves the parent's promise with
// the chosen card.
// ──────────────────────────────────────────────────────────────────────

const formatRemaining = (endIso) => {
  if (!endIso) return '—';
  const ms = new Date(endIso).getTime() - Date.now();
  if (ms <= 0) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const PRManagerCardPicker = ({
  open,
  language = 'en',
  cards = [], // [{card_id, title, image_url, image_focus_x/y, exhibit_ends, estimated_hours, estimated_card_xp, fee_coins, rarity}]
  onPick,     // (card) -> void
  onClose,
}) => {
  const [shouldRender, setShouldRender] = useState(open);

  useEffect(() => {
    if (open) { setShouldRender(true); return; }
    if (!shouldRender) return;
    const id = setTimeout(() => setShouldRender(false), 220);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!shouldRender) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [shouldRender]);

  if (!shouldRender) return null;

  const exiting = !open;
  const t = language === 'ru'
    ? { title: 'Выберите картину', subtitle: 'Сейчас на выставке', empty: 'Нет картин на выставке. Сначала выставьте картину в галерее.' }
    : { title: 'Pick a painting', subtitle: 'Currently on exhibit', empty: 'No paintings on exhibit. Send one to a show first.' };

  return (
    <div
      data-testid="pr-card-picker"
      className={`fixed inset-0 z-[70] flex items-end sm:items-center justify-center ${
        exiting ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'
      }`}
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-[3px]" onClick={onClose} />
      <div
        className={`relative w-full sm:max-w-sm bg-[#0E0E18] border border-[#7DD3FC]/30 rounded-t-2xl sm:rounded-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.7)] px-4 pt-3 pb-5 max-h-[80vh] overflow-y-auto ${
          exiting ? 'animate-out slide-out-to-bottom duration-200' : 'animate-in slide-in-from-bottom duration-300'
        }`}
        style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-[#3F3F46]/70 mb-3 sm:hidden" />

        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-heading text-[17px] text-white leading-tight">{t.title}</h2>
            <div className="text-[10px] text-[#A1A1AA]/70 uppercase tracking-[0.18em] mt-0.5">
              {t.subtitle}
            </div>
          </div>
          <button
            data-testid="pr-picker-close"
            onClick={onClose}
            className="p-1 rounded-full hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4 text-[#A1A1AA]" />
          </button>
        </div>

        {cards.length === 0 ? (
          <p className="text-[12px] text-[#A1A1AA]/70 text-center py-8 px-2 leading-relaxed">
            {t.empty}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {cards.map((c) => (
              <button
                key={c.card_id}
                data-testid={`pr-pick-${c.card_id}`}
                onClick={() => onPick && onPick(c)}
                className="relative aspect-square rounded-xl overflow-hidden border-2 border-[#7DD3FC]/35 hover:border-[#7DD3FC] active:scale-95 transition-all text-left"
              >
                <img
                  src={thumb(c.image_url, 160)}
                  alt={c.title || ''}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: `${c.image_focus_x ?? 50}% ${c.image_focus_y ?? 50}%` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-1 right-1 px-1.5 py-[1px] rounded-full bg-black/65 text-[#7DD3FC] text-[8px] font-mono font-bold flex items-center gap-0.5">
                  <Clock className="w-2.5 h-2.5" />
                  {formatRemaining(c.exhibit_ends)}
                </span>
                <div className="absolute inset-x-1 bottom-1">
                  <div className="text-[10px] text-white font-semibold truncate">{c.title}</div>
                  <div className="text-[9px] text-[#7DD3FC] flex items-center gap-0.5 font-mono tabular-nums">
                    <ChessRook className="w-2.5 h-2.5" />+{c.estimated_card_xp} XP
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PRManagerCardPicker;
